import React, { useMemo, useState } from "react";
import {
  Drawer,
  Card,
  CardContent,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Box,
  Dialog,
  DialogContent,
  MenuItem,
  DialogContentText,
  DialogActions,
  TextField,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useStyles } from "../../theme/appStyles";
import {
  IRequest,
  IRequestResponents,
  IUserAdmin,
} from "../../Api/Interface/api.interface";
import {
  useCreateRespontantUserMutation,
  useGetRequestDrawerDataQuery,
  useGetRequestQuery,
  useGetUserInfoOnyQuery,
} from "../../Api/attoDeskApi";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs, { Dayjs } from "dayjs";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import { useNotifier } from "../../Core/Notifier";
import useIsMobile from "../../Core/hook/useIsMobile";
import { requestStatus } from "../../Core/Enum/enum";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface IProfileProps {
  notification: HTMLElement | null;
  onClose: () => void;
  open: boolean;
}

const formatTime = (time: string | undefined) => {
  if (!time) return "";
  const [hour, minute] = time.split(":").map(Number);
  const date = new Date();
  date.setHours(hour, minute);
  return new Intl.DateTimeFormat("en-CA", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  }).format(date);
};

const headerHeight = 75;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  minHeight: headerHeight,
  padding: theme.spacing(0, 1),
  backgroundColor: theme.palette.topbarButton.drawerHeader,
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  flexShrink: 0,
}));

const DrawerContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  flexGrow: 1,
  "&::-webkit-scrollbar": {
    width: "0px",
    color: "#fcf2eb",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#fcf2eb",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#fcf2eb",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
}));

const Profile: React.FC<IProfileProps> = ({ notification, onClose, open }) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobileRes = useIsMobile();
  const [newStatus, { isLoading }] = useCreateRespontantUserMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [openOrderListDate, setOpenOrderListDate] = useState(false);
  const [requestToDelete, setrequestToDelete] = useState<IRequest | null>(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmHeading, setConfirmHeading] = useState("");
  const [confirmStatus, setConfirmStatus] = useState("");

  const { data: userData, isLoading: userIsLoading } = useGetUserInfoOnyQuery({
    searchText: "",
  });

  const {
    data: drawerData,
    isLoading: drawerIsLoading,
    isError,
    refetch,
  } = useGetRequestDrawerDataQuery({
    id: "",
  });

  const userList = useMemo(() => {
    return userData?.data as IUserAdmin[];
  }, [userData?.data]);

  const userMap = useMemo(() => {
    const map = new Map();
    userList?.forEach((user) => {
      map.set(user.id, `${user.user_role_name}`);
    });
    return map;
  }, [userList]);

  const coursings: IRequest[] = Array.isArray(drawerData?.data)
    ? (drawerData?.data as IRequest[])
    : [];

  const formik = useFormik({
    initialValues: {
      requestId: String(requestToDelete?.id) || "",
      status: confirmStatus,
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          status: values.status,
          // status:confirmStatus,
          requestId: values.requestId,
        };

        const addCompanyResponse = await newStatus(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          resetForm();
          setOpenOrderListDate(false);
          setrequestToDelete(null);
          refetch();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
    return (
      formik.values.status !== "" &&
      formik.values.status !== undefined &&
      formik.values.requestId !== "" &&
      formik.values.requestId !== undefined
    );
  }, [formik]);

  const handleOpenDialogGenerate = (
    coursing: IRequest,
    status: string,
    heading: string,
    message: string
  ) => {
    setrequestToDelete(coursing);
    setConfirmHeading(heading);
    setConfirmMessage(message);
    setConfirmStatus(status);
    setOpenOrderListDate(true);
  };

  const handleCloseDialogGenerate = () => {
    setOpenOrderListDate(false);
    setrequestToDelete(null);
    setConfirmMessage("");
    setConfirmHeading("");
    setConfirmStatus("");
    formik.resetForm();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "80%", sm: 450 },
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <DrawerHeader>
        <Grid container spacing={2}>
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            className={classes.drawerNotification}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "16px",
                flexShrink: 0,
              }}
            >
              <Typography variant="h5" className={classes.drawerHeaderText}>
                Notifications
              </Typography>
              <IconButton
                onClick={onClose}
                className={classes.drawerHeaderText}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </Grid>
        </Grid>
      </DrawerHeader>
      <DrawerContent>
        <Grid container spacing={2}>
          {drawerIsLoading || userIsLoading ? (
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{
                margin: "6px",
              }}
            >
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="10vh"
              >
                <CircularProgress />
              </Box>
            </Grid>
          ) : isError || coursings.length === 0 ? (
            <Grid
              item
              lg={12}
              md={12}
              sm={12}
              xs={12}
              sx={{
                margin: "6px",
              }}
            >
              <Typography sx={{ textAlign: "center", marginTop: "12px" }}>
                {isError ? "Error fetching data" : "No new notifications"}
              </Typography>
            </Grid>
          ) : (
            coursings.map((row: IRequest, index) => (
              <Grid
                key={index}
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{
                  margin: "6px",
                }}
              >
                <Card variant="outlined" className={classes.card}>
                  <CardContent sx={{ cursor: "pointer" }}>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item>
                        <Grid container spacing={1} direction="column">
                          <Grid item>
                            <Typography variant="subtitle1" fontWeight={600}>
                              From: {userMap.get(row?.created_by)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Grid item xs={12} sm={12}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          noWrap
                        >
                          Subject: {row?.formatted_subject || row?.subject}
                        </Typography>
                      </Grid>
                      {row?.schedule_date && (
                        <Grid item xs={12} sm={12}>
                          <Typography variant="body1">
                            Date:{" "}
                            {dayjs(row?.schedule_date).format("MMM-D, YYYY") ||
                              ""}
                          </Typography>
                        </Grid>
                      )}
                      {row?.startTime && row?.endTime && (
                        <Grid item xs={12} sm={12}>
                          <Typography variant="body1">
                            Time: {formatTime(row.startTime)} -{" "}
                            {formatTime(row.endTime)}
                            {row.spansNextDay == true && (
                              <span
                                style={{
                                  color: "#e3791a",
                                  fontWeight: 600,
                                  lineHeight: 1.5,
                                  fontSize: "0.875rem",
                                  paddingLeft: "1px",
                                }}
                              >
                                [next day]
                              </span>
                            )}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>

                  <Divider />

                  <CardContent>
                    <Typography
                      component="div"
                      dangerouslySetInnerHTML={{
                        __html: row?.formatted_message || "",
                      }}
                    />
                  </CardContent>
                  <CardContent>
                    <Grid container spacing={1}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={() => {
                            handleOpenDialogGenerate(
                              row,
                              "Cancel",
                              "Request Cancelled",
                              "Are you sure you want to cancel this schedule request?"
                            );
                          }}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          onClick={() => {
                            handleOpenDialogGenerate(
                              row,
                              "Accepted",
                              "Schedule Request Confirmation",
                              "Are you sure you want to proceed with this schedule request?"
                            );
                          }}
                          className={classes.addButton}
                        >
                          Accept
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <Dialog
          open={openOrderListDate}
          onClose={handleCloseDialogGenerate}
          sx={{ padding: 5, minWidth: 450 }}
        >
          <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
            <form onSubmit={formik.handleSubmit}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    {confirmHeading}
                  </Typography>
                </Grid>
              </Grid>
              <DialogContent>
                <DialogContentText sx={{ py: 3 }}>
                  {confirmMessage}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  className={classes.addButton}
                  onClick={handleCloseDialogGenerate}
                >
                  Cancel
                </Button>
                <Box m={0.5}></Box>

                <Button
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon />}
                  className={classes.addButton}
                  onClick={() => formik.handleSubmit()}
                  disabled={!formValid || isLoading}
                >
                  Confirm
                </Button>
              </DialogActions>
            </form>
          </Grid>
        </Dialog>
      </DrawerContent>
    </Drawer>
  );
};

export default Profile;
